// ─────────────────────────────────────────────────────────────────────────
// Ecoffee Cup® South Africa — design tokens, written out for Figma.
// Every value here matches tailwind.config.js and index.css exactly.
// ─────────────────────────────────────────────────────────────────────────

export type Swatch = {
  /** Figma colour style name, e.g. "Core/Black Coffee" */
  figma: string
  name: string
  hex: string
  rgb: string
  token: string
  usage: string
  /** Text colour to lay over the swatch */
  on: 'light' | 'dark'
}

export const coreColours: Swatch[] = [
  {
    figma: 'Core/Black Coffee',
    name: 'Black Coffee',
    hex: '#3B302F',
    rgb: '59, 48, 47',
    token: 'coffee',
    usage: 'Primary brand colour. Headlines, primary buttons, dark sections.',
    on: 'dark',
  },
  {
    figma: 'Core/Purple Pennant',
    name: 'Purple Pennant',
    hex: '#432C47',
    rgb: '67, 44, 71',
    token: 'pennant',
    usage: 'Accent for eyebrows and links. Choose Your Story sections.',
    on: 'dark',
  },
  {
    figma: 'Core/Oat Milk',
    name: 'Oat Milk',
    hex: '#D3C9BE',
    rgb: '211, 201, 190',
    token: 'oat',
    usage: 'Hairline borders, dividers, inactive chips, skeletons.',
    on: 'light',
  },
  {
    figma: 'Core/Yves Klein',
    name: 'Yves Klein',
    hex: '#171C8F',
    rgb: '23, 28, 143',
    token: 'klein',
    usage: 'Sparingly, for high-contrast moments and travel-led stories.',
    on: 'dark',
  },
]

export const neutralColours: Swatch[] = [
  {
    figma: 'Neutral/Paper',
    name: 'Paper',
    hex: '#F3EEE7',
    rgb: '243, 238, 231',
    token: 'paper',
    usage: 'Default page background. Text colour on dark sections.',
    on: 'light',
  },
  {
    figma: 'Neutral/Paper Warm',
    name: 'Paper Warm',
    hex: '#EDE6DB',
    rgb: '237, 230, 219',
    token: 'paper-warm',
    usage: 'Alternating section background, card fills, inset panels.',
    on: 'light',
  },
  {
    figma: 'Neutral/Ink',
    name: 'Ink',
    hex: '#2B2422',
    rgb: '43, 36, 34',
    token: 'ink',
    usage: 'Body copy. Used at 55–70% opacity, never at full strength.',
    on: 'dark',
  },
  {
    figma: 'Neutral/Warm Gray',
    name: 'Warm Gray',
    hex: '#6E6259',
    rgb: '110, 98, 89',
    token: 'warm-gray',
    usage: 'Secondary metadata and muted UI text.',
    on: 'dark',
  },
]

export const accentColours: Swatch[] = [
  {
    figma: 'Accent/Pink Glo',
    name: 'Pink Glo',
    hex: '#FA008A',
    rgb: '250, 0, 138',
    token: 'pink-glo',
    usage: 'Loud product colourways, sale and launch moments.',
    on: 'dark',
  },
  {
    figma: 'Accent/Red',
    name: 'Red',
    hex: '#DA291C',
    rgb: '218, 41, 28',
    token: 'brand-red',
    usage: 'Warnings, “don’t” lists, urgent labels.',
    on: 'dark',
  },
  {
    figma: 'Accent/Radiant Yellow',
    name: 'Radiant Yellow',
    hex: '#FC9E21',
    rgb: '252, 158, 33',
    token: 'radiant-yellow',
    usage: 'Warm story ranges — Karoo, sunflower colourways.',
    on: 'light',
  },
  {
    figma: 'Accent/Mid Blue',
    name: 'Mid Blue',
    hex: '#0077C8',
    rgb: '0, 119, 200',
    token: 'mid-blue',
    usage: 'Coastal stories — Cape Point, ocean colourways.',
    on: 'dark',
  },
  {
    figma: 'Accent/Dark Green',
    name: 'Dark Green',
    hex: '#284734',
    rgb: '40, 71, 52',
    token: 'forest',
    usage: 'Sustainability sections, fynbos and botanical ranges.',
    on: 'dark',
  },
  {
    figma: 'Accent/Conch Shell',
    name: 'Conch Shell',
    hex: '#EFA6AA',
    rgb: '239, 166, 170',
    token: 'conch',
    usage: 'Soft product tints and gifting panels.',
    on: 'light',
  },
  {
    figma: 'Accent/Bleached Aqua',
    name: 'Bleached Aqua',
    hex: '#BCE3DF',
    rgb: '188, 227, 223',
    token: 'aqua',
    usage: 'Light tinted panels, matcha and cold-drink contexts.',
    on: 'light',
  },
  {
    figma: 'Accent/Navy',
    name: 'Navy',
    hex: '#041E42',
    rgb: '4, 30, 66',
    token: 'navy',
    usage: 'Deep alternative to Black Coffee for night-led stories.',
    on: 'dark',
  },
]

// ── Typography ───────────────────────────────────────────────────────────
export type TypeStyle = {
  figma: string
  sample: string
  family: string
  weight: string
  size: string
  lineHeight: string
  tracking: string
  transform: string
  className: string
  /** Preview class applied to the sample text */
  preview: string
}

export const typeStyles: TypeStyle[] = [
  {
    figma: 'Display/Hero',
    sample: 'A cup worth carrying',
    family: 'DM Serif Display',
    weight: 'Regular (400)',
    size: '60px desktop · 40px mobile',
    lineHeight: '100%',
    tracking: '-3% (-30)',
    transform: 'Sentence case',
    className: 'type-display text-6xl',
    preview: 'type-display text-5xl md:text-6xl leading-none text-coffee',
  },
  {
    figma: 'Display/H1',
    sample: 'Three sizes. One of them is yours.',
    family: 'DM Serif Display',
    weight: 'Regular (400)',
    size: '48px desktop · 36px mobile',
    lineHeight: '100%',
    tracking: '-3% (-30)',
    transform: 'Sentence case',
    className: 'type-display text-5xl',
    preview: 'type-display text-4xl md:text-5xl leading-none text-coffee',
  },
  {
    figma: 'Display/H2',
    sample: 'Ranges drawn from South Africa.',
    family: 'DM Serif Display',
    weight: 'Regular (400)',
    size: '36px',
    lineHeight: '110%',
    tracking: '-3% (-30)',
    transform: 'Sentence case',
    className: 'type-display text-4xl',
    preview: 'type-display text-4xl text-coffee',
  },
  {
    figma: 'Display/H3 — card title',
    sample: 'Custom sleeves',
    family: 'DM Serif Display',
    weight: 'Regular (400)',
    size: '24px',
    lineHeight: '120%',
    tracking: '-3% (-30)',
    transform: 'Sentence case',
    className: 'type-display text-2xl',
    preview: 'type-display text-2xl text-coffee',
  },
  {
    figma: 'Eyebrow/Large',
    sample: 'Choose Your Story',
    family: 'Gotham Bold (web: Montserrat Bold)',
    weight: 'Bold (700)',
    size: '11px',
    lineHeight: '140%',
    tracking: '+20% (+200)',
    transform: 'ALL CAPS',
    className: 'type-eyebrow text-[11px]',
    preview: 'type-eyebrow text-[11px] text-pennant',
  },
  {
    figma: 'Eyebrow/Small',
    sample: 'Minimum order',
    family: 'Gotham Bold (web: Montserrat Bold)',
    weight: 'Bold (700)',
    size: '9px',
    lineHeight: '140%',
    tracking: '+20% (+200)',
    transform: 'ALL CAPS',
    className: 'type-eyebrow text-[9px]',
    preview: 'type-eyebrow text-[9px] text-coffee/45',
  },
  {
    figma: 'Body/Large',
    sample:
      'Bamboo fibre cups in three sizes, printed with the work of artists we love.',
    family: 'Gotham Light (web: Montserrat Light)',
    weight: 'Light (300)',
    size: '18px',
    lineHeight: '170%',
    tracking: '-2% (-20)',
    transform: 'Sentence case',
    className: 'type-body text-lg',
    preview: 'type-body text-lg text-ink/65',
  },
  {
    figma: 'Body/Base',
    sample:
      'The right size depends on what you drink and how long you take over it.',
    family: 'Gotham Light (web: Montserrat Light)',
    weight: 'Light (300)',
    size: '16px',
    lineHeight: '170%',
    tracking: '-2% (-20)',
    transform: 'Sentence case',
    className: 'type-body text-base',
    preview: 'type-body text-base text-ink/65',
  },
  {
    figma: 'Body/Small',
    sample: 'Lids and sleeves are sold separately in every colourway we make.',
    family: 'Gotham Light (web: Montserrat Light)',
    weight: 'Light (300)',
    size: '14px',
    lineHeight: '170%',
    tracking: '-2% (-20)',
    transform: 'Sentence case',
    className: 'type-body text-sm',
    preview: 'type-body text-sm text-ink/65',
  },
  {
    figma: 'CTA/Primary',
    sample: 'Shop now',
    family: 'Gotham Medium (web: Montserrat Medium)',
    weight: 'Medium (500)',
    size: '11px',
    lineHeight: '100%',
    tracking: '+9% (+90)',
    transform: 'ALL CAPS',
    className: 'type-cta text-[11px]',
    preview: 'type-cta text-[11px] text-coffee',
  },
  {
    figma: 'CTA/Small',
    sample: 'Enquire about this',
    family: 'Gotham Medium (web: Montserrat Medium)',
    weight: 'Medium (500)',
    size: '10px',
    lineHeight: '100%',
    tracking: '+9% (+90)',
    transform: 'ALL CAPS',
    className: 'type-cta text-[10px]',
    preview: 'type-cta text-[10px] text-pennant',
  },
  {
    figma: 'UI/Label',
    sample: 'Truth Coffee, Cape Town',
    family: 'Gotham Medium (web: Montserrat Medium)',
    weight: 'Medium (500)',
    size: '14–16px',
    lineHeight: '150%',
    tracking: '-1% (-10)',
    transform: 'Sentence case',
    className: 'type-ui text-base',
    preview: 'type-ui text-base text-coffee',
  },
]

// ── Radii, shadows, layout ───────────────────────────────────────────────
export const radii = [
  { name: 'Pill', value: '999px', usage: 'All buttons, chips, filter pills.' },
  { name: 'Small', value: '16px', usage: 'Thumbnails, inputs, small tiles.' },
  { name: 'Medium', value: '24px', usage: 'Standard content cards, panels.' },
  {
    name: 'Large',
    value: '32px',
    usage: 'Feature cards, image frames, dark CTA blocks.',
  },
  { name: 'X-Large', value: '40px', usage: 'Hero imagery and full-bleed panels.' },
]

export const shadows = [
  {
    name: 'Effect/Soft',
    value: '0 20px 60px -25px rgba(43,36,34,0.25)',
    usage: 'Floating panels, dropdown menus.',
  },
  {
    name: 'Effect/Card',
    value: '0 12px 40px -18px rgba(43,36,34,0.28)',
    usage: 'Raised cards on hover.',
  },
]

export const layoutRules = [
  { label: 'Max content width', value: '1400px' },
  { label: 'Page gutter', value: '24px' },
  { label: 'Grid', value: '12 column · 24–32px gap' },
  { label: 'Section padding', value: '80px mobile · 96–112px desktop' },
  { label: 'Sticky header height', value: '73px' },
  { label: 'Anchor scroll offset', value: '112px (scroll-mt-28)' },
  { label: 'Product image ratio', value: '4:5 portrait' },
  { label: 'Editorial image ratio', value: '4:3 · 16:9 for heroes' },
]

export const figmaNotes = [
  {
    title: 'Colour styles',
    body: 'Create one style per swatch using the Figma names above (Core/…, Neutral/…, Accent/…). Body copy is Ink at 55–70% opacity and muted labels are Black Coffee at 45% — set those with layer opacity on the style, not as new colours.',
  },
  {
    title: 'Text styles',
    body: 'Twelve text styles cover the whole site. Name them exactly as listed so the hierarchy stays legible: Display/…, Eyebrow/…, Body/…, CTA/…, UI/Label.',
  },
  {
    title: 'Fonts to install',
    body: 'DM Serif Display is free on Google Fonts. Gotham is licensed — if the Figma file has it, use Gotham Light, Medium and Bold. If not, Montserrat Light/Medium/Bold is the substitute used in this build.',
  },
  {
    title: 'Do not use',
    body: 'Phosphate Solid, Budmojiggler and Craque Medium Melted are retail and social only. They never appear on web pages, EDMs or documentation.',
  },
]
